import { getFileNameFromPath, isImagePath, normalizeFileExtension, toFileNameSlug } from 'sharedKernel/file/path'

export interface ScoutingOpportunityFile {
  id?: string
  name?: string
  path?: string
  type?: string
  extension?: string
  mimetype?: string
}

const INVALID_COVER_PATHS = new Set(['string', 'null', 'undefined', 'none', '-'])

const isValidCoverPath = (path?: string | null): path is string => {
  if (!path?.trim()) return false
  const trimmed = path.trim()
  if (INVALID_COVER_PATHS.has(trimmed.toLowerCase())) return false
  return true
}

const readOpportunityFileField = (
  file: Record<string, unknown>,
  keys: string[]
): string => {
  for (const key of keys) {
    const value = file[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return ''
}

export const normalizeOpportunityFile = (
  file: Record<string, unknown> | ScoutingOpportunityFile
): ScoutingOpportunityFile => {
  const source = file as Record<string, unknown>

  return {
    id: readOpportunityFileField(source, ['id', 'Id']) || undefined,
    name: readOpportunityFileField(source, ['name', 'Name', 'originalName', 'OriginalName']) || undefined,
    path: readOpportunityFileField(source, ['path', 'Path']) || undefined,
    type: readOpportunityFileField(source, ['type', 'Type', 'fileType', 'FileType']) || undefined,
    extension: readOpportunityFileField(source, ['extension', 'Extension']) || undefined,
    mimetype: readOpportunityFileField(source, ['mimetype', 'Mimetype', 'mimeType', 'MimeType']) || undefined,
  }
}

export const getOpportunityFiles = (
  opportunity: { files?: unknown[] } | null | undefined
): ScoutingOpportunityFile[] => {
  const embedded = opportunity?.files
  if (!Array.isArray(embedded)) return []

  return embedded
    .map(file => normalizeOpportunityFile(file as Record<string, unknown>))
    .filter(file => Boolean(file.path))
}

export const isOpportunityCoverImageFile = (file: ScoutingOpportunityFile): boolean =>
  (file.type ?? '').toLowerCase() === 'coverimage'

export const isOpportunitySupportFile = (file: ScoutingOpportunityFile): boolean => {
  const type = (file.type ?? '').toLowerCase()
  if (type === 'coverimage') return false
  if (type === 'supportfiles' || type === 'supportmaterials') return true
  if (!type && file.path) return !isImagePath(file.path)
  return false
}

export const getOpportunitySupportFileDisplayName = (
  file: ScoutingOpportunityFile,
  opportunityTitle?: string
): string => {
  if (file.name?.trim()) return file.name.trim()

  const fromPath = getFileNameFromPath(file.path)
  if (fromPath) return fromPath

  const normalizedExtension = normalizeFileExtension(file.extension, file.path)
  const baseName = toFileNameSlug(opportunityTitle ?? '')
  if (baseName) return `${baseName}${normalizedExtension}`

  return normalizedExtension ? normalizedExtension.slice(1) : ''
}

export const getOpportunityCoverPathFromFiles = (
  files?: ScoutingOpportunityFile[]
): string | undefined => {
  if (!files?.length) return undefined

  const coverFile =
    files.find(isOpportunityCoverImageFile) ??
    files.find((file) => isImagePath(file.path))

  if (coverFile?.path && isValidCoverPath(coverFile.path)) {
    return coverFile.path
  }

  return undefined
}

export const getOpportunitySupportFiles = (
  fileRows: ScoutingOpportunityFile[] | null | undefined,
  opportunity: { files?: unknown[] } | null | undefined
): ScoutingOpportunityFile[] => {
  const normalizedRows = fileRows?.map(file => normalizeOpportunityFile(file)) ?? []
  const fromApi = normalizedRows.filter(isOpportunitySupportFile)

  if (fromApi.length > 0) return fromApi

  const embedded = getOpportunityFiles(opportunity).filter(isOpportunitySupportFile)
  if (embedded.length > 0) return embedded

  return []
}

export const isValidOpportunityCoverPath = isValidCoverPath
